import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useFireProjection, usePensionSettings, useUpdatePensionSettings } from "../hooks/pension";
import type { UpdatePensionSettingsInput } from "../lib/pension";

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

export function PensionFire() {
  const { t } = useTranslation();
  const settings = usePensionSettings();
  const updateSettings = useUpdatePensionSettings();
  const fire = useFireProjection();

  const [targetMode, setTargetMode] = useState<"EXPENSE_25X" | "MANUAL">("EXPENSE_25X");
  const [manualTargetEur, setManualTargetEur] = useState("");
  const [annualExpenseEurOverride, setAnnualExpenseEurOverride] = useState("");
  const [assumedRealReturnPercent, setAssumedRealReturnPercent] = useState("5.0");
  const [assumedInflationPercent, setAssumedInflationPercent] = useState("2.0");
  const [targetRetirementAge, setTargetRetirementAge] = useState("67");
  const [birthYear, setBirthYear] = useState("");
  const [monthlyContributionEurOverride, setMonthlyContributionEurOverride] = useState("");

  useEffect(() => {
    if (!settings.data) return;
    const s = settings.data;
    setTargetMode(s.targetMode);
    setManualTargetEur(s.manualTargetCents !== null ? String(s.manualTargetCents / 100) : "");
    setAnnualExpenseEurOverride(
      s.annualExpenseCentsOverride !== null ? String(s.annualExpenseCentsOverride / 100) : "",
    );
    setAssumedRealReturnPercent((s.assumedRealReturnBp / 100).toFixed(2));
    setAssumedInflationPercent((s.assumedInflationBp / 100).toFixed(2));
    setTargetRetirementAge(String(s.targetRetirementAge));
    setBirthYear(s.birthYear !== null ? String(s.birthYear) : "");
    setMonthlyContributionEurOverride(
      s.monthlyContributionCentsOverride !== null
        ? String(s.monthlyContributionCentsOverride / 100)
        : "",
    );
  }, [settings.data]);

  async function handleSaveSettings(e: React.FormEvent) {
    e.preventDefault();
    const input: UpdatePensionSettingsInput = {
      targetMode,
      manualTargetCents: manualTargetEur ? Math.round(Number(manualTargetEur) * 100) : null,
      annualExpenseCentsOverride: annualExpenseEurOverride
        ? Math.round(Number(annualExpenseEurOverride) * 100)
        : null,
      assumedRealReturnBp: Math.round(Number(assumedRealReturnPercent) * 100),
      assumedInflationBp: Math.round(Number(assumedInflationPercent) * 100),
      targetRetirementAge: Number(targetRetirementAge),
      birthYear: birthYear ? Number(birthYear) : null,
      monthlyContributionCentsOverride: monthlyContributionEurOverride
        ? Math.round(Number(monthlyContributionEurOverride) * 100)
        : null,
    };
    await updateSettings.mutateAsync(input);
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--muted)" }}>
            <Link to="/pension" className="hover:underline">
              {t("nav.pension")}
            </Link>
            <span>/</span>
            <span style={{ color: "var(--text)" }}>FIRE</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("pension.fire.title")}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t("pension.fire.intro")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <form onSubmit={handleSaveSettings} className="fv-card p-5 space-y-3 text-sm">
          <h2 className="text-base font-semibold">{t("pension.fire.settings_title")}</h2>

          <Field label={t("pension.fire.birth_year")}>
            <Input
              type="number"
              min={1900}
              max={2025}
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
            />
          </Field>
          <Field label={t("pension.fire.target_retirement_age")}>
            <Input
              type="number"
              min={50}
              max={80}
              value={targetRetirementAge}
              onChange={(e) => setTargetRetirementAge(e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pension.fire.real_return")}>
              <Input
                type="number"
                step="0.1"
                value={assumedRealReturnPercent}
                onChange={(e) => setAssumedRealReturnPercent(e.target.value)}
              />
            </Field>
            <Field label={t("pension.fire.inflation")}>
              <Input
                type="number"
                step="0.1"
                value={assumedInflationPercent}
                onChange={(e) => setAssumedInflationPercent(e.target.value)}
              />
            </Field>
          </div>

          <Field label={t("pension.fire.target_mode")}>
            <Select
              value={targetMode}
              onChange={(e) => setTargetMode(e.target.value as "EXPENSE_25X" | "MANUAL")}
            >
              <option value="EXPENSE_25X">{t("pension.fire.target_mode_25x")}</option>
              <option value="MANUAL">{t("pension.fire.target_mode_manual")}</option>
            </Select>
          </Field>

          {targetMode === "MANUAL" && (
            <Field label={t("pension.fire.manual_target")}>
              <Input
                type="number"
                step="100"
                min={0}
                value={manualTargetEur}
                onChange={(e) => setManualTargetEur(e.target.value)}
              />
            </Field>
          )}
          {targetMode === "EXPENSE_25X" && (
            <Field label={t("pension.fire.annual_expense_override")}>
              <Input
                type="number"
                step="100"
                min={0}
                placeholder={t("pension.fire.from_cashflow_placeholder")}
                value={annualExpenseEurOverride}
                onChange={(e) => setAnnualExpenseEurOverride(e.target.value)}
              />
            </Field>
          )}

          <Field label={t("pension.fire.monthly_contribution_override")}>
            <Input
              type="number"
              step="10"
              min={0}
              placeholder={t("pension.fire.from_cashflow_placeholder")}
              value={monthlyContributionEurOverride}
              onChange={(e) => setMonthlyContributionEurOverride(e.target.value)}
            />
          </Field>

          <button
            type="submit"
            disabled={updateSettings.isPending}
            className="px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: "var(--accent)" }}
          >
            {t("pension.fire.save")}
          </button>
        </form>

        <div className="space-y-3">
          {fire.data && (
            <>
              <KpiCard
                label={t("pension.fire.kpi.current_networth")}
                value={formatEur(fire.data.currentNetworthCents)}
              />
              <KpiCard
                label={t("pension.fire.kpi.target")}
                value={formatEur(fire.data.targetCents)}
                tone="accent"
              />
              <KpiCard
                label={t("pension.fire.kpi.monthly_contribution")}
                value={formatEur(fire.data.monthlyContributionCents)}
                sub={t("pension.fire.kpi.monthly_contribution_sub")}
              />
              <KpiCard
                label={t("pension.fire.kpi.years_to_target")}
                value={
                  fire.data.isAlreadyAtTarget
                    ? t("pension.fire.kpi.already_at_target")
                    : fire.data.yearsToTarget !== null
                      ? t("pension.fire.kpi.years", { count: fire.data.yearsToTarget })
                      : "—"
                }
                sub={
                  fire.data.projectedTargetAge !== null
                    ? t("pension.fire.kpi.target_age", { age: fire.data.projectedTargetAge })
                    : undefined
                }
                tone={fire.data.isAlreadyAtTarget ? "pos" : undefined}
              />
              {fire.data.warnings.length > 0 && (
                <div
                  className="fv-card p-3 text-xs space-y-1"
                  style={{ background: "var(--warn-soft)", borderColor: "var(--warn)" }}
                >
                  <p className="font-semibold" style={{ color: "var(--warn)" }}>
                    {t("pension.fire.warnings_title")}
                  </p>
                  {fire.data.warnings.map((w) => (
                    <p key={w} style={{ color: "var(--warn)" }}>
                      {t(`pension.fire.warning.${w}`, w)}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {fire.data && (
        <div className="fv-card p-5">
          <h2 className="text-base font-semibold mb-3">{t("pension.fire.chart_title")}</h2>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart
                data={fire.data.projection}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid stroke="var(--grid)" strokeDasharray="3 3" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: "var(--muted)" }} />
                <YAxis
                  tickFormatter={(v: number) => `${Math.round(v / 100_000) / 10}M`}
                  tick={{ fontSize: 11, fill: "var(--muted)" }}
                  width={56}
                />
                <Tooltip
                  formatter={(v: number) => formatEur(v)}
                  labelFormatter={(l: string | number) => `Jahr ${l}`}
                  contentStyle={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    color: "var(--text)",
                  }}
                />
                <ReferenceLine
                  y={fire.data.targetCents}
                  stroke="var(--pos)"
                  strokeDasharray="4 4"
                  label={{
                    value: t("pension.fire.chart_target_label"),
                    fill: "var(--pos)",
                    fontSize: 11,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: "var(--muted)" }} />
                <Line
                  type="monotone"
                  dataKey="networthCents"
                  name={t("pension.fire.chart_networth")}
                  stroke="var(--accent)"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="block text-sm">
      <span
        className="text-xs font-semibold uppercase tracking-wider"
        style={{ color: "var(--muted)" }}
      >
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="w-full px-2 py-1.5 rounded-lg text-sm"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    />
  );
}

function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className="w-full px-2 py-1.5 rounded-lg text-sm"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    >
      {props.children}
    </select>
  );
}

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "pos" | "neg" | "accent";
}) {
  const color =
    tone === "pos"
      ? "var(--pos)"
      : tone === "neg"
        ? "var(--neg)"
        : tone === "accent"
          ? "var(--accent)"
          : "var(--text)";
  return (
    <div className="fv-card p-4">
      <div className="fv-kpi-label mb-1">{label}</div>
      <div className="text-xl font-bold tracking-tight num" style={{ color }}>
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}
